import { IAppContext } from 'App/AppContext';
import { apiGetLink } from 'services/Api/linkService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import { graphGetIDriveItemVersions, graphUpdateFile } from 'services/Graph/graphServiceDrive';
import { DocumentMimeTypes } from './copilotDocumentBlockHelpers';

// SharePoint drive coordinates for a library-backed document, resolved from the library link.
export interface LibraryDriveCoordinates {
  linkId: number;
  driveId: string;
  driveItemId: string;
  altTenantId?: string;
}

// A marker of a SharePoint drive-item version. `version` is the human-readable SharePoint version label
// (the "0.59" / "0.60" _UIVersionString shown in the library grid), taken from the drive item's version
// history -- NOT the cTag/eTag, which are opaque content-change identifiers. Used to record version-before
// at start and version-after at completion so the caller can detect "already applied" and later manual changes.
export interface SharePointVersionMarker {
  version?: string;
  modified?: string;
}

// Resolve the SharePoint drive coordinates for a library link. Returns undefined when the link is not a
// drive-backed document (no drive id / drive item id).
export const resolveLibraryDriveCoordinates = async (
  appContext: IAppContext,
  linkId: number,
): Promise<LibraryDriveCoordinates | undefined> => {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  const link = await apiGetLink(linkId, accessToken, appContext.globalDataCache);
  const driveId = link?.list?.spDriveId;
  const driveItemId = link?.driveItemId;
  if (!driveId || !driveItemId) return undefined;

  return { linkId, driveId, driveItemId, altTenantId: link?.list?.altTenantId };
};

// Read the current SharePoint version label for a library document from its version history (latest first).
// Best-effort: returns an empty marker when the history cannot be read (the version fields are advisory,
// used only for warnings and change detection).
export const readLibrarySharePointVersion = async (
  appContext: IAppContext,
  coordinates: LibraryDriveCoordinates,
): Promise<SharePointVersionMarker> => {
  try {
    const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, coordinates.altTenantId);
    const versions = await graphGetIDriveItemVersions(graphInterface.client, coordinates.driveId, coordinates.driveItemId);
    const latest = versions[0];

    return { version: latest?.id, modified: latest?.lastModifiedDateTime };
  } catch {
    return {};
  }
};

// Write an edited .docx buffer straight to the SharePoint library file (in-place content update, which
// creates a new SharePoint version), then return the new version label from the version history. Throws on
// upload failure so the caller can fail the run instead of recording a false success.
export const uploadDocxBufferToLibrary = async (
  appContext: IAppContext,
  coordinates: LibraryDriveCoordinates,
  buffer: ArrayBuffer,
  title: string | undefined,
): Promise<SharePointVersionMarker> => {
  const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, coordinates.altTenantId);
  const file = new File([buffer], `${title ?? 'document'}.docx`, { type: DocumentMimeTypes.docx });
  await graphUpdateFile(graphInterface.client, file, coordinates.driveId, coordinates.driveItemId);

  // The content PUT returns a DriveItem (cTag/eTag), not the friendly version label, so read the history for
  // the new "0.60"-style label the library grid shows.
  return readLibrarySharePointVersion(appContext, coordinates);
};
